import React from 'react';
import { TextField, MenuItem, Box, Divider, Select } from '@mui/material';
import DialogGlynt, { FormControlStyled, FormLabelStyled } from '../../common/Dialog/DialogGlynt';
import AddNewButton from './components/AddNewButton';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import TemplateColumnsList from './components/TemplateColumnsList';
import TemplateFiltersList from './components/TemplateFiltersList';
import TemplateOutputOptions from './components/TemplateOutputOptions';
import TemplateNamingSection from './components/TemplateNamingSection';
import PackagerConfigUnsavedChanges from './PackagerConfigUnsavedChanges';
import useTemplateEditorForm from './useTemplateEditorForm';
import useGetPackagerAttributes from '../../hooks/packager/useGetPackagerAttributes';
import { OutputTemplate, TemplateReportType } from '../../../types/packager-types';

interface TemplateEditorDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (name: string, template: OutputTemplate) => void;
  editingTemplate: { name: string; template: OutputTemplate } | null;
  existingTemplateNames: string[];
  dataPoolId: string;
  packagerId: string;
}

const TemplateEditorDialog: React.FC<TemplateEditorDialogProps> = ({
  open,
  onClose,
  onSave,
  editingTemplate,
  existingTemplateNames,
  dataPoolId,
  packagerId,
}) => {
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);

  const { data: attributes } = useGetPackagerAttributes(dataPoolId, packagerId);
  // DOCUMENT_DATA naming options from the validator-backed subset the API exposes.
  const documentDataOptions = React.useMemo(
    () => (attributes?.document_data_fields || []).map((f: string) => ({ value: f, label: f })),
    [attributes]
  );

  const form = useTemplateEditorForm({
    open,
    editingTemplate,
    existingTemplateNames,
    dataPoolId,
    packagerId,
    onSave,
    onClose,
  });

  // Reset unsaved dialog when dialog closes
  React.useEffect(() => {
    if (!open) {
      setShowUnsavedDialog(false);
    }
  }, [open]);

  const handleCloseWithCheck = () => {
    if (form.state.isDirty) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedDialog(false);
    onClose();
  };

  const handleCancelClose = () => {
    setShowUnsavedDialog(false);
  };

  return (
    <DialogGlynt
      open={open}
      closeModal={handleCloseWithCheck}
      width="700px"
      cypressId="template-editor-dialog"
    >
      <DialogTitleGlynt
        title={editingTemplate ? 'Edit Template' : 'Add Template'}
        closeModal={handleCloseWithCheck}
      />
      <DialogContentGlynt scrollable>
        {/* Template Name & Report Type */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControlStyled sx={{ flex: 1, mt: 0 }}>
            <FormLabelStyled>TEMPLATE NAME</FormLabelStyled>
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="e.g., disney_standard"
              value={form.state.templateName}
              onChange={(e) => form.setTemplateName(e.target.value)}
              error={!!form.state.nameError}
              helperText={form.state.nameError}
            />
          </FormControlStyled>
          <FormControlStyled sx={{ flex: 1, mt: 0 }}>
            <FormLabelStyled>DATA SOURCE (REPORT TYPE)</FormLabelStyled>
            <Select
              size="small"
              fullWidth
              variant="outlined"
              value={form.state.reportType}
              onChange={(e) => form.handleReportTypeChange(e.target.value as TemplateReportType)}
            >
              <MenuItem value={TemplateReportType.ACTIVITY}>Activity Data</MenuItem>
              <MenuItem value={TemplateReportType.MAT}>MAT (Master Asset Table)</MenuItem>
              <MenuItem value={TemplateReportType.CT}>CT (Customization Table)</MenuItem>
              <MenuItem value={TemplateReportType.MAT_CT}>MAT CT (Combined)</MenuItem>
              <MenuItem value={TemplateReportType.COC}>CoC (Chain of Custody)</MenuItem>
              <MenuItem value={TemplateReportType.VALIDATION}>Validation Report</MenuItem>
            </Select>
          </FormControlStyled>
        </Box>

        {/* Columns */}
        <Box sx={{ width: '100%', mt: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <FormLabelStyled sx={{ mb: 0 }}>COLUMNS</FormLabelStyled>
            <AddNewButton onClick={form.addColumn} />
          </Box>
          <TemplateColumnsList
            columns={form.state.columns}
            fields={form.availableFields}
            onUpdate={form.updateColumn}
            onRemove={form.removeColumn}
            onMove={form.moveColumn}
            error={form.state.columnsError}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Filters */}
        <TemplateFiltersList
          filters={form.state.filters}
          fields={form.availableFields}
          onAdd={form.addFilter}
          onUpdate={form.updateFilter}
          onUpdateValue={form.updateFilterValue}
          onRemove={form.removeFilter}
        />

        <Divider sx={{ my: 2 }} />

        {/* Output Options */}
        <TemplateOutputOptions
          fileFormat={form.state.fileFormat}
          splitBy={form.state.splitBy}
          defaultSplitValue={form.state.defaultSplitValue}
          splitByFields={form.splitByFields}
          onFileFormatChange={form.handleFileFormatChange}
          onSplitByChange={form.handleSplitByChange}
          onDefaultSplitValueChange={form.handleDefaultSplitValueChange}
        />

        <Divider sx={{ my: 2 }} />

        {/* File Naming */}
        <TemplateNamingSection
          namingElements={form.state.namingElements}
          namingSeparator={form.state.namingSeparator}
          namingMaxLength={form.state.namingMaxLength}
          splitBy={form.state.splitBy}
          error={form.state.namingError}
          onAdd={form.addNamingElement}
          onUpdate={form.updateNamingElement}
          onRemove={form.removeNamingElement}
          onMove={form.moveNamingElement}
          onSeparatorChange={form.handleNamingSeparatorChange}
          onMaxLengthChange={form.handleNamingMaxLengthChange}
          getNamingPreview={form.getNamingPreview}
          documentDataOptions={documentDataOptions}
        />
      </DialogContentGlynt>
      <DialogActionsGlynt
        handleSubmit={form.handleSave}
        closeModal={handleCloseWithCheck}
        cancelCypressId="btn-template-editor-cancel"
        submitCypressId="btn-template-editor-save"
        clearCypressId="btn-template-editor-clear"
        showClearButton={false}
        submitButtonText="Save"
      />

      <PackagerConfigUnsavedChanges
        open={showUnsavedDialog}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </DialogGlynt>
  );
};

export default TemplateEditorDialog;
